import { Fragment, useState } from 'react';
import DateTimePickerWrapper from './datetime-picker-lib';
import moment, { Moment } from 'moment';
import { isSameDay } from 'utils/date-format';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import ToastContainer, { ToastType } from 'components/toasts/container';
import { Icon } from 'components/ui/icon';
import './dateRangePicker.css';

interface IProps {
  setStartDatetime: React.Dispatch<React.SetStateAction<Moment>>;
  setEndDatetime: React.Dispatch<React.SetStateAction<Moment>>;
  initialStartValue?: Moment;
  initialEndValue?: Moment;
}

const DateTimeRangePicker = ({
  initialStartValue,
  initialEndValue,
  setStartDatetime,
  setEndDatetime,
}: IProps) => {
  const startDatetime = initialStartValue || moment(new Date()).startOf('day');
  const endDatetime =
    initialEndValue || moment(new Date()).subtract(30, 'days').endOf('day');
  const now = new Date();
  const maxDate = moment(now).endOf('day');
  const isOrgLevel = useOrgLevel();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const applyCallback = (startDate: Moment, endDate: Moment) => {
    if (isOrgLevel) {
      const daysDiff = endDate.diff(startDate, 'days');

      if (daysDiff > 31) {
        setToastMessage(
          'Org-level view supports a maximum date range of 31 days'
        );
        setShowToast(true);
        return;
      }
    }

    setStartDatetime(startDate);
    setEndDatetime(endDate);
  };

  const ranges: Record<string, [Moment, Moment]> = {
    Today: [moment(now).startOf('day'), moment(now).endOf('day')],
    Yesterday: [
      moment(now).subtract(1, 'days').startOf('day'),
      moment(now).subtract(1, 'days').endOf('day'),
    ],
    'Last 7 Days': [
      moment(now).subtract(7, 'days').startOf('day'),
      moment(now).endOf('day'),
    ],
    'Last 30 Days': [
      moment(now).subtract(30, 'days').startOf('day'),
      moment(now).endOf('day'),
    ],
  };

  if (!isOrgLevel) {
    ranges['Last 90 Days'] = [
      moment(now).subtract(90, 'days').startOf('day'),
      moment(now).endOf('day'),
    ];
  }

  ranges['Last week'] = [
    moment(now).subtract(1, 'weeks').startOf('week'),
    moment(now).subtract(1, 'weeks').endOf('week'),
  ];
  ranges['Last month'] = [
    moment(now).subtract(1, 'months').startOf('month'),
    moment(now).subtract(1, 'months').endOf('month'),
  ];

  const local = {
    format: 'MM/DD/YYYY hh:mm A',
    sundayFirst: true,
  };

  const style = {
    betweenDates: { color: '#333D4E', backgroundColor: '#F2F3F4' },
    hoverCell: { color: '#333D4E', backgroundColor: '#F2F3F4' },
    customRangeButtons: { backgroundColor: '#008053', color: '#FFFFFF' },
    fromDate: { backgroundColor: '#008053', color: '#FFFFFF' },
    toDate: { backgroundColor: '#008053', color: '#FFFFFF' },
    customRangeSelected: { backgroundColor: '#008053', color: '#FFFFFF' },
    standaloneLayout: { display: 'flex', maxWidth: 'fit-content' },
  };

  const getDisplayDateRange = () => {
    const dateFormat = 'MMM DD, YYYY';
    const timeFormat = 'hh:mm A';
    const datetimeFormat = 'MMM DD, YYYY hh:mm A';

    if (isSameDay(startDatetime, endDatetime)) {
      return `${moment(startDatetime).format(dateFormat)} ${moment(
        startDatetime
      ).format(timeFormat)} - ${moment(endDatetime).format(timeFormat)}`;
    }

    return `${moment(startDatetime).format(datetimeFormat)} - ${moment(
      endDatetime
    ).format(datetimeFormat)}`;
  };

  return (
    <Fragment>
      <div className="date-range-picker-wrapper">
        <DateTimePickerWrapper
          ranges={ranges}
          start={startDatetime}
          end={endDatetime}
          local={local}
          applyCallback={applyCallback}
          noMobileMode
          twelveHoursClock
          style={style}
          maxDate={maxDate}
          smartMode
          pastSearchFriendly
        >
          <div className="flex items-center h-[38px] px-3 py-2 rounded border border-gray-blue-20 bg-white shadow-sm cursor-pointer">
            <Icon
              name="calendar"
              size={20}
              className="text-gray-blue-50"
              aria-hidden="true"
            />
            <span className="text-gray-blue-80 text-sm font-normal leading-5 ml-2 whitespace-nowrap">
              {getDisplayDateRange()}
            </span>
          </div>
        </DateTimePickerWrapper>
      </div>
      <ToastContainer
        showToast={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={ToastType.error}
      />
    </Fragment>
  );
};

export default DateTimeRangePicker;
